import { Box, Divider, Skeleton, Stack } from '@mui/material';
import { ReactElement } from 'react';
import ViewWrapper from 'components/core/Layout/ViewWrapper';
import { Color } from 'shared/utils/colors';
import {
  inspectionGridFullWidthItemSx,
  inspectionGridSx,
  inspectionProtocolWrapperWithFooterSpacerSx,
  inspectionSectionPaddingSx,
} from 'components/inspection/layout';

const FIELD_COUNT = 8;
const CHECKLIST_SECTIONS = 2;
const ITEMS_PER_SECTION = 2;

function FieldSkeleton(): ReactElement {
  return (
    <Box>
      <Skeleton variant='text' width='38%' height={18} />
      <Skeleton variant='rounded' height={56} />
    </Box>
  );
}

function AccordionRowSkeleton({
  section = false,
}: {
  section?: boolean;
}): ReactElement {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        minHeight: 48,
        px: 2,
        mb: 1,
        border: '1px solid',
        borderColor: Color.border,
        bgcolor: section ? 'grey.100' : 'background.paper',
      }}
    >
      <Skeleton variant='text' width={section ? '32%' : 28} height={24} />
      {section ? null : <Skeleton variant='text' width='42%' height={24} />}
      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
        {section ? (
          <Skeleton variant='rounded' width={112} height={28} sx={{ borderRadius: 999 }} />
        ) : null}
        <Skeleton variant='circular' width={24} height={24} sx={{ ml: 2 }} />
      </Box>
    </Box>
  );
}

export function SingleInspectionSkeleton(): ReactElement {
  return (
    <ViewWrapper>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{ ...inspectionSectionPaddingSx, mt: 4, mb: 4 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton variant='circular' width={40} height={40} />
          <Skeleton variant='text' width={180} height={36} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton variant='rounded' width={128} height={36} />
          <Skeleton variant='rounded' width={36} height={36} />
        </Box>
      </Stack>

      <Divider />

      <Box sx={{ ...inspectionSectionPaddingSx, mt: 4, mb: 4 }}>
        <Skeleton variant='text' width='46%' height={32} />
      </Box>

      <Box sx={inspectionGridSx}>
        {Array.from({ length: FIELD_COUNT }, (_, index) => (
          <FieldSkeleton key={index} />
        ))}
        <Box sx={inspectionGridFullWidthItemSx}>
          <Skeleton variant='text' width='18%' height={18} />
          <Skeleton variant='rounded' height={120} />
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      <Box sx={{ ...inspectionSectionPaddingSx, my: 2 }}>
        <Skeleton variant='text' width='28%' height={32} />
      </Box>
      <Box sx={inspectionGridSx}>
        <Box sx={inspectionGridFullWidthItemSx}>
          <Skeleton variant='text' width='70%' height={24} />
          <Skeleton variant='text' width='55%' height={24} />
          <Skeleton variant='rounded' width={160} height={96} sx={{ mt: 1 }} />
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      <Box sx={inspectionProtocolWrapperWithFooterSpacerSx}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 4,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Skeleton variant='text' width={160} height={32} sx={{ my: 2 }} />
            <Skeleton variant='text' width={120} height={24} />
          </Box>
          <Skeleton variant='rounded' width={148} height={36} />
        </Box>

        {Array.from({ length: CHECKLIST_SECTIONS }, (_, sectionIndex) => (
          <Box key={sectionIndex}>
            <AccordionRowSkeleton section />
            {Array.from({ length: ITEMS_PER_SECTION }, (_, itemIndex) => (
              <AccordionRowSkeleton key={itemIndex} />
            ))}
          </Box>
        ))}
      </Box>
    </ViewWrapper>
  );
}
